<template>
	<article class="buybox">
		<div class="buybox__heading">
			<h2 v-if="!noHeader" class="buybox__heading__text">
				<slot name="buybox-title">
					{{ $t('WEBAPP_WATCHNOW') }}
				</slot>
			</h2>
			<div class="switchers-container">
				<slot name="buybox-switcher"></slot>
				<div class="buybox__heading__country-switcher" v-if="!isBannerBuyBox && !inline">
					<BuyBoxCountrySwitcher :hideText="true" :additionalContexts="additionalContexts" />
				</div>
			</div>
		</div>

		<section
			v-if="!hideUntilLoaded || !loading || cachedResults"
			class="buybox__content"
			:class="{
				inline,
				'rent-offers-first': hasActiveJwtvCampaign,
				'grid-category-split': isGridCategorySplitActive,
			}"
		>
			<GridBuyBoxFilter
				v-if="showFilter"
				id="regular-buybox"
				class="buybox__filter"
				:isInModal="isInModal"
				:defaultPresentationType="defaultPresentationType"
				:additionalContexts="additionalContexts"
				:isEpisodeBuybox="isEpisodeBuybox"
				:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
				@episodeFilterChange="setEpisodeBuyboxPresentationType"
			/>

			<NoOfferRow
				v-if="hasNoOffersToShow && isFreeBuyBox"
				:title="title"
				:isTitleInWatchlist="isTitleInWatchlist"
				:titleHasNoOffers="hasNoNonFreeOffers"
				:inline="inline"
				:isEpisodeBuybox="isEpisodeBuybox"
				:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
				@onWatchlistToggle="emits('onWatchlistToggle', $event)"
				:compact="compact"
			>
				<template v-if="isFreeBuyBox">
					{{ $t('WEBAPP_FREE') }}
				</template>
			</NoOfferRow>

			<!-- Grid Redesign Experiments - CTA & More Info - Rows -->
			<template v-if="renderGridRedesign">
				<GridOfferRowRedesignExp
					v-for="row in offerRows"
					v-show="row.show"
					:key="row.key"
					:class="row.class"
					:title="title"
					:offers="row.offers"
					:bundles="row.bundles || []"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isAVODActive="isAVODActive"
					:isBannerBuyBox="isBannerBuyBox"
					:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
					:isEpisodeBuybox="isEpisodeBuybox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					{{ $t(row.label) }}
				</GridOfferRowRedesignExp>
			</template>
			<!-- Grid Redesign Experiments: Rows -->

			<template v-else>
				<OfferRow
					v-if="!isGridCategorySplitActive"
					class="stream"
					:title="title"
					:offers="isFreeBuyBox ? filteredOffersFree : filteredOffersSorted"
					:bundles="isFreeBuyBox ? [] : filteredOffers.bundles"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isAVODActive="isAVODActive"
					:isBannerBuyBox="isBannerBuyBox"
					:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
					:isEpisodeBuybox="isEpisodeBuybox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					<template v-if="isFreeBuyBox">
						{{ $t('WEBAPP_FREE') }}
					</template>
				</OfferRow>
				<OfferRow
					v-if="isGridCategorySplitActive"
					class="subscription"
					:title="title"
					:offers="subscriptionOffers"
					:bundles="filteredOffers.bundles"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isAVODActive="isAVODActive"
					:isBannerBuyBox="isBannerBuyBox"
					:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
					:isEpisodeBuybox="isEpisodeBuybox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					<template>
						<span :style="language === 'en' ? { fontSize: '12px' } : {}">
							{{ subscriptionLabel }}
						</span>
					</template>
				</OfferRow>

				<OfferRow
					v-if="isGridCategorySplitActive"
					class="free"
					:title="title"
					:offers="filteredOffersFree"
					:bundles="[]"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isAVODActive="isAVODActive"
					:isBannerBuyBox="isBannerBuyBox"
					:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
					:isEpisodeBuybox="isEpisodeBuybox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					<template>
						{{ $t('WEBAPP_FREE') }}
					</template>
				</OfferRow>

				<OfferRow
					v-show="!isFreeBuyBox"
					class="rent"
					:title="title"
					:offers="filteredOffers.rent"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isBannerBuyBox="isBannerBuyBox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					{{ $t('WEBAPP_RENT') }}
				</OfferRow>

				<OfferRow
					v-show="!isFreeBuyBox"
					class="buy"
					:title="title"
					:offers="filteredOffers.buy"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isBannerBuyBox="isBannerBuyBox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					{{ $t('WEBAPP_BUY') }}
				</OfferRow>

				<OfferRow
					v-show="!isFreeBuyBox"
					class="cinema"
					:title="title"
					:offers="filteredOffers.cinema"
					:inline="inline"
					:additionalContexts="trackingContexts"
					:isBannerBuyBox="isBannerBuyBox"
					:activePresentationType="buyBoxActiveType"
					@clickOut="clickOut"
				>
					{{ $t('WEBAPP_CINEMA') }}
				</OfferRow>

				<FastOfferRow
					v-show="showFastRow"
					:title="title"
					:offers="fastOffers"
					:inline="inline"
					:additionalContexts="trackingContexts"
					@clickOut="clickOut"
				/>
			</template>

			<NoOfferRow
				v-if="hasNoOffersToShow && !isFreeBuyBox"
				:title="title"
				:isTitleInWatchlist="isTitleInWatchlist"
				:titleHasNoOffers="hasNoNonFreeOffers"
				:inline="inline"
				:isEpisodeBuybox="isEpisodeBuybox"
				:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
				@onWatchlistToggle="emits('onWatchlistToggle', $event)"
				:compact="compact"
			>
				<template v-if="isFreeBuyBox">
					{{ $t('WEBAPP_FREE') }}
				</template>
			</NoOfferRow>

			<GridRowLabel v-if="renderSponsoredRecommendation" class="offers no-border" :label="$t('WEBAPP_OTHERS')">
				<SponsoredRecommendation
					:sponsoredAd="sponsoredAd"
					:currentTitle="title"
					placement="sr_buybox"
					:lazy="false"
					:sraPlacement="SraPlacement.Buybox"
					:buyBoxType="NewBuyBoxView.GRID"
				/>
			</GridRowLabel>

			<!-- Pre Roll & Static BB Ads Experiment -->
			<GridPreRollExp
				v-if="expPreRollActive"
				:title="title"
				:catalogueItem="catalogueItem"
				:providerShortName="promotionProviderShortName"
				:shortName="promotionShortName"
				:isFreeBuyBox="isFreeBuyBox"
				:additionalContexts="additionalContexts"
				:trackingContexts="trackingContexts"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
			/>
			<!-- Pre Roll & Static BB Ads Experiment -->

			<GridPromotionRow
				v-if="showPromotionRow && !qualityTvActive && freeTrialActive"
				:title="title"
				:offers="[...filteredOffers.free, ...filteredOffers.stream]"
				:inline="inline"
				:hasNoOffers="hasNoNonFreeOffers || hasOnlyCinemaOffers"
				:additionalContexts="trackingContexts"
				@clickOut="clickOut"
			/>

			<GridSwiperExp
				v-if="expGridSwiperActive"
				:catalogueItem="catalogueItem"
				:title="title"
				:offers="[]"
				:additionalContexts="additionalContexts"
			/>

			<QualityTvRow
				v-if="qualityTvActive"
				class="offers no-border"
				:providerShortName="promotionProviderShortName"
				:shortName="promotionShortName"
				:title="title"
				:isFreeBuyBox="isFreeBuyBox"
				:additionalContexts="additionalContexts"
				:trackingContexts="trackingContexts"
				:seasonNumber="seasonNumber"
				:episodeNumber="episodeNumber"
			>
				{{ $t('WEBAPP_OTHERS') }}
			</QualityTvRow>

			<GridRowLabel v-if="showSQTV" class="offers no-border" :label="$t('WEBAPP_OTHERS')">
				<GridSQTV
					:providerShortName="promotionProviderShortName"
					:catalogueItem="catalogueItem"
					:shortName="promotionShortName"
					:title="title"
					:isFreeBuyBox="isFreeBuyBox"
					:additionalContexts="additionalContexts"
					:trackingContexts="trackingContexts"
					:seasonNumber="seasonNumber"
					:episodeNumber="episodeNumber"
				/>
			</GridRowLabel>

			<p class="buybox-text">
				<span v-if="lastUpdated && isTitleDetailPage && !inline" class="text-low buybox__content__timestamp">{{
					timestampText
				}}</span>
				<OfferReporter
					v-if="!inline"
					:titleObjectId="titleObjectId"
					:titleObjectType="titleObjectType"
					:titleName="titleName"
					:titleId="titleId"
					:originalReleaseYear="originalReleaseYear"
					:titleOffers="offerReporterTitleOffers"
					:titleScoring="titleScoring"
					:additionalContexts="additionalContexts"
				/>
			</p>
		</section>

		<!-- Only to collect shadow events as there's no active SQTV for old buybox. -->
		<SponsoredRecommendation
			v-if="isShadowCampaign && isSREnabled"
			:sponsoredAd="sponsoredAd"
			placement="sr_buybox"
			:sraPlacement="SraPlacement.Buybox"
			:buyBoxType="NewBuyBoxView.GRID"
		/>
	</article>
</template>

<script lang="ts" setup>
import { computed, onMounted, provide, ref, watch } from 'vue';

import {
	GetTitleOffersDocument,
	GetTitleOffersQuery,
	GetTitleOffersQueryVariables,
} from '@/features/buybox/graphql/queries/GetTitleOffers.query';

import { ImpressionTrackingEvents } from '@/enums/events';
import { OfferLocation } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { timestamp } from '@/helpers/date-helper';

import type { TitleOffers } from '@/interfaces/buybox';

import {
	ObjectType,
	Offer as OfferGraphql,
	Scoring,
	SraPlacement,
	MonetizationType,
	PresentationType,
	SraEventAction,
} from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';

import { getExcludedPackageShortNames } from '@/constants/package-exclusion';

import { CountriesToBeSorted, sortOffersByMonetizationPotential } from '@/features/buybox/helpers/buy-box-helpers';

import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import GridBuyBoxFilter from '@/features/buybox/components/grid/GridBuyBoxFilter.vue';
import OfferRow from '@/features/buybox/components/grid/OfferRow.vue';
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';
import { useGridCategorySplit } from '@/features/buybox/composables/useBuyboxView';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useExperiment } from '@/libraries/experiment';
import GridRowLabel from '@/features/buybox/components/grid/GridRowLabel.vue';
import GridSwiperExp from '@/components/experiments/GridSwiperExp/GridSwiperExp.vue';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import GridPreRollExp from '@/components/experiments/PreRollAndStaticBBAdsExp/GridPreRollExp.vue';

const OfferReporter = defineAsyncComponent(() => import('@/features/buybox/components/OfferReporter.vue'));
const FastOfferRow = defineAsyncComponent(() => import('@/features/buybox/components/grid/FastOfferRow.vue'));
const NoOfferRow = defineAsyncComponent(() => import('@/features/buybox/components/grid/NoOfferRow.vue'));
const GridPromotionRow = defineAsyncComponent(() => import('@/features/buybox/components/grid/GridPromotionRow.vue'));
const QualityTvRow = defineAsyncComponent(() => import('@/features/buybox/components/grid/QualityTvRow.vue'));
const GridSQTV = defineAsyncComponent(() => import('@/features/buybox/components/grid/GridSQTV.vue'));
const GridOfferRowRedesignExp = defineAsyncComponent(
	() => import('@/components/experiments/GridBuyboxRedesignExps/components/GridOfferRowRedesignExp.vue')
);
const SponsoredRecommendation = defineAsyncComponent(
	() => import('@/features/sponsored-recommendations/components/SponsoredRecommendation.vue')
);

const props = withDefaults(
	defineProps<{
		titleId: string;
		titleObjectType: ObjectType;
		titleObjectId: number;
		titleName?: string;
		title: TitleDetail;
		// You need to pass this prop when title is Season/Episode, because BuyBox component has functionality to set
		// title into Watchlist, but this mutation available only for Movie/Show. So basically when you set Season/Episode
		// to Watchlist we set whole Show to watchlist.
		showDetails?: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null;
		isTitleInWatchlist?: boolean;
		titleScoring?: Scoring;
		seasonNumber?: number | null;
		episodeNumber?: number | null;
		originalReleaseYear?: number | null;
		sponsoredAd?: SponsoredAdFragment;
		isBannerBuyBox?: boolean;
		additionalContexts?: SnowplowContext[];
		buyBoxSwitchType?: NewBuyBoxView;
		showFilter?: boolean;
		inline?: boolean;
		noHeader?: boolean;
		compact?: boolean;
		defaultPresentationType?: BuyboxPresentationType;
		isInModal?: boolean;
		isAVODActive?: boolean;
		isEpisodeBuybox?: boolean;
		placement?: ClickoutPlacement;
		index?: number;
		hideUntilLoaded?: boolean;
	}>(),
	{
		titleName: '',
		seasonNumber: null,
		episodeNumber: null,
		showDetails: null,
		originalReleaseYear: undefined,
		sponsoredAd: undefined,
		placement: 'regular_buybox',
		additionalContexts: () => [],
		titleScoring: () => ({}),
	}
);

const emits = defineEmits<{
	(e: 'onWatchlistToggle', createdAt: string | null): void;
	(e: 'buyboxLoading', isLoading: boolean): void;
}>();

const episodeBuyBoxPresentationType = ref<BuyboxPresentationType | null>(null);

const { titleProviderCount } = storeToRefs(useConstantStore());
const { language, country } = storeToRefs(useLanguageStore());

const userStore = useUserStore();
const { setBuyboxPresentationType } = userStore;
const { buyboxPresentationType, isPremium, isLoggedIn, isProUser } = storeToRefs(userStore);
const { pagesVisited } = storeToRefs(useTrackingStore());

const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();
const { trackSraEvent } = useBackendTracking();

const { hasActiveJwtvCampaign } = useJWTVCampaign();

/** Boolean value to only enable SQTV SRs on Buybox rendered on title detail page (Excludes: banners, search and editorial).
 * Notify SG Team if SQTV SR is to be extended to search page */
const isSREnabled = computed(() => {
	const routeName = activeRoute.value?.name;
	const isDetailPage = routeName?.includes('app.detail');
	const isSearchPage = routeName?.includes('app.search.list');

	return !!(isDetailPage && !isSearchPage && !props.inline && !props.isBannerBuyBox);
});

const { sponsoredAd, isShadowCampaign, hasActiveSRCampaign } = useSendBidId({
	pageType: 'VIEW_BUYBOX',
	placement: SraPlacement.Buybox,
	jwEntityID: props.title.id,
	isEnabled: isSREnabled,
	isSeo: pagesVisited.value <= 1,
	buyBoxType: NewBuyBoxView.GRID,
});

const hasSponsoredRecommendation = computed(() => {
	return hasActiveSRCampaign.value && !isShadowCampaign.value;
});

useProvideClickoutPlacement(() => props.placement);

provide('isBannerBuyBox', () => props.isBannerBuyBox);
provide('titleSourceId', props.titleId);
provide('buyboxIndex', () => props.index);

const buyBoxActiveType = computed(() => {
	return props.isEpisodeBuybox ? episodeBuyBoxPresentationType.value : buyboxPresentationType.value;
});

const presentationTypeGraphql = computed(() => {
	const presentationTypeMap: Partial<Record<PresentationType, PresentationType[]>> = {
		[PresentationType['4K']]: [PresentationType['4K'], PresentationType.Bluray_4K],
		[PresentationType.Hd]: [PresentationType.Hd, PresentationType.Bluray],
		[PresentationType.Sd]: [PresentationType.Sd, PresentationType.Dvd],
		[PresentationType.Dvd]: [PresentationType.Dvd],
		[PresentationType.Bluray]: [PresentationType.Bluray, PresentationType.Bluray_4K],
		[PresentationType.Canvas]: [PresentationType.Canvas],
	};

	if (buyBoxActiveType.value == null) return null;
	if (buyBoxActiveType.value == MonetizationType.Free) return null;
	return presentationTypeMap[buyBoxActiveType.value];
});

const queryVariables = computed(() => {
	//prettier-ignore
	const presentationTypes = (presentationTypeGraphql.value ?? [])?.length > 0 ? presentationTypeGraphql.value : undefined;
	const dvdPresentationTypes = presentationTypes
		? presentationTypes
		: [PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K];
	return {
		nodeId: props.titleId,
		country: country.value,
		language: language.value,
		filterBuy: {
			monetizationTypes: [MonetizationType.Buy],
			bestOnly: true,
			preAffiliate: true,
			presentationTypes,
		},
		filterFlatrate: {
			monetizationTypes: [
				MonetizationType.Flatrate,
				MonetizationType.FlatrateAndBuy,
				MonetizationType.Ads,
				MonetizationType.Free,
				MonetizationType.Cinema,
			],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterRent: {
			monetizationTypes: [MonetizationType.Rent],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterFree: {
			monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterDvdBluRay: {
			monetizationTypes: [MonetizationType.Rent, MonetizationType.Buy],
			presentationTypes: dvdPresentationTypes,
			preAffiliate: true,
		},
	};
});

const { loading, result, onResult } = useQuery<GetTitleOffersQuery, GetTitleOffersQueryVariables>(
	GetTitleOffersDocument,
	queryVariables,
	{
		fetchPolicy: 'cache-first' as const,
		nextFetchPolicy: 'cache-first' as const,
	}
);
// Cached Results are used to avoid flickering when user changes the selected Tab as it triggers the query again and result set to null.
const cachedResults = ref<GetTitleOffersQuery | null>(null);
onResult(data => {
	if (!data.loading) {
		cachedResults.value = data.data ?? null;
	}
});

const offers = computed(() => {
	const node = (result.value ?? cachedResults.value)?.node;
	if (
		node &&
		(node.__typename === 'Episode' ||
			node.__typename === 'Movie' ||
			node.__typename === 'Season' ||
			node.__typename === 'Show')
	) {
		const { fast, free, flatrate, offerCount, maxOfferUpdatedAt, bundles } = node;
		const { rent, buy } = getRentAndBuyOffers(node);
		const res: TitleOffers = {
			stream: [],
			rent: rent,
			buy: buy,
			free: free,
			fast: fast,
			cinema: [],
			bundles: bundles as TitleOffers['bundles'],
			offerCount,
			maxOfferUpdatedAt,
		};
		flatrate.forEach(offer =>
			offer.monetizationType === MonetizationType.Cinema ? res.cinema.push(offer) : res.stream.push(offer)
		);
		return res;
	}

	return {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		bundles: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
});

const { inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => {
		const { free = [], stream } = offers.value;
		return [...free, ...stream];
	},
});

// Grid Swiper Exp
const { activeVariantWithControlGroup: expGridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');
const expGridSwiperActive = computed(
	() => !props.inline && expGridSwiperVariant.value && expGridSwiperVariant.value !== 'control'
);

// Grid Swiper Exp

// Pre Roll & Static BB Ads Exp
const { activeVariantWithControlGroup: expPreRollVariant } = useExperiment('EXP_PRE_ROLL_GRID');
const expPreRollActive = computed(
	() => !props.inline && expPreRollVariant.value && expPreRollVariant.value !== 'control'
);
// Pre Roll & Static BB Ads Exp

// Grid Redesign Exps

const { activeVariantWithControlGroup: gridRedesignCTAVariant } = useExperiment('EXP_GRID_REDESIGN_CLICK');
const { activeVariantWithControlGroup: gridMoreInfoVariant } = useExperiment('EXP_GRID_REDESIGN_MORE_INFO');

// Apple Binge Exp
const { activeVariantWithControlGroup: appleBingeExpVariant } = useExperiment('EXP_APPLE_BINGE_2');
// Apple Binge Exp

const renderGridRedesign = computed(() => {
	if (props.inline) return false;
	if (!!gridRedesignCTAVariant.value) return gridRedesignCTAVariant.value !== 'control';
	return gridMoreInfoVariant.value && gridMoreInfoVariant.value !== 'control';
});

const renderSponsoredRecommendation = computed(
	() => hasSponsoredRecommendation.value && isSREnabled.value && !gridRedesignCTAVariant.value
);

const isFreeBuyBox = computed(() => {
	return buyBoxActiveType.value === MonetizationType.Free;
});

const excludedPackages = computed(() => {
	const titleObjectId = props.title.__typename === 'Season' ? props.title?.show.id : props.title.id;
	return getExcludedPackageShortNames(isLoggedIn.value, titleObjectId, country.value, isProUser.value);
});

const filteredOffers = computed(() => {
	// Start with the full list of offers
	let _offers = { ...offers.value };

	// Filtering out TVOD providers we don't have a partnership with
	const { offerCount, maxOfferUpdatedAt, bundles, ...allOffers } = _offers;

	let filteredOffers = Object.fromEntries(
		Object.entries(allOffers).map(([key, value]) => [
			key,
			value.filter(offer => !excludedPackages.value.includes(offer.package.shortName)),
		])
	);

	// If the title has a JWTV offer and there is an active JWTV campaign, only show JWTV offers.
	const isJWTVTitleCampaign =
		offers.value.rent.some(offer => offer.package.shortName === 'jwt') && hasActiveJwtvCampaign.value;

	if (isJWTVTitleCampaign) {
		filteredOffers = Object.fromEntries(
			Object.entries(filteredOffers).map(([key, value]) => [
				key,
				value.filter(offer => offer.package.shortName === 'jwt'),
			])
		);
	}

	_offers = {
		...filteredOffers,
		bundles,
		offerCount,
		maxOfferUpdatedAt,
	} as TitleOffers;

	if (!props.isBannerBuyBox) return _offers;

	// Return filtered offers
	return sortOffersByMonetizationPotential(_offers, country.value as CountriesToBeSorted);
});

// Tubi Test >> for US only, sort buybox offers to show Tubi First
const filteredOffersFree = computed(() => filteredOffers.value.free);

const filteredOffersSorted = computed(() => filteredOffers.value.stream);
// Tubi Test

/** Excludes Free. The title does not have stream, rent, buy, or any other offers. */
const hasNoNonFreeOffers = computed(() => {
	return (
		(['stream', 'rent', 'buy', 'fast', 'cinema'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0
	);
});

const hasOnlyCinemaOffers = computed(() => {
	const cinemaOfferCount = filteredOffers.value.cinema.length;

	const hasNoNonCinemaOffers =
		(['stream', 'rent', 'buy', 'fast', 'free', 'fast', 'bundles'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0;

	return cinemaOfferCount > 0 && hasNoNonCinemaOffers;
});

const showPromotionRow = computed(() => {
	const rawLanguage = language.value.split('-')[0];
	return props.title && !isPremium.value && isFreeBuyBox.value && allowedPromotionLanguages.includes(rawLanguage);
});

const titleContext = computed(() => {
	const titleObjectId = props.showDetails ? props.showDetails.objectId : props.titleObjectId;
	const titleObjectType = props.showDetails ? props.showDetails.objectType : props.titleObjectType;
	return new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber,
		props.episodeNumber,
		null,
		props.index
	);
});

provide('titleContext', titleContext);

const hasNoOffersToShow = computed(() => {
	// this buybox filter is about Monetization, so here specifically we ask
	// if there are no "FREE" offers only
	if (isFreeBuyBox.value) {
		const hasNoFreeOffers = filteredOffers.value.free.length === 0;

		// Temporary - we are disabling FT when SR active, until the swiper is available
		if (renderSponsoredRecommendation.value) return hasNoFreeOffers;

		// if there is a promotion from a provider that does have an offer,
		// then technically the promotion counts as an "offer".
		if (showPromotionRow.value) {
			return !inPromotionCatalogue.value && hasNoFreeOffers;
		}

		return hasNoFreeOffers;
	}

	return hasNoNonFreeOffers.value || hasOnlyCinemaOffers.value;
});

const lastUpdated = computed(() => {
	return filteredOffers.value.maxOfferUpdatedAt;
});

const isTitleDetailPage = computed(() => {
	return activeRoute.value?.name?.includes('app.detail');
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

const sponsoredRecommendationPackageId = computed(() => {
	return props.sponsoredAd?.campaign?.watchNowOffer.package.packageId;
});

const sponsoredRecommendationCohort = computed(() => {
	return props.sponsoredAd?.holdoutGroup ? 0 : 1;
});

const offerReporterTitleOffers = computed(() => {
	return [
		...filteredOffers.value.free,
		...filteredOffers.value.stream,
		...filteredOffers.value.rent,
		...filteredOffers.value.buy,
		...filteredOffers.value.cinema,
	];
});

const fastOffers = computed(() => {
	// Exclude Offers that are no longer active.
	return filteredOffers.value.fast.filter(el => new Date(el.availableToTime) > new Date());
});

const showFastRow = computed(() => {
	return isFreeBuyBox.value || buyBoxActiveType.value === null;
});

const trackingContexts = computed(() => (props.additionalContexts || []).concat([titleContext.value]));

const hideTVODQTV = computed(() => {
	return (
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		isFreeBuyBox.value
	);
});

const promotionProviderShortName = computed(() => catalogueItem.value?.shortName);

const qualityTvActive = computed(() => {
	if (hasSponsoredRecommendation.value) return false;
	if (expGridSwiperActive.value) return false;
	if (gridRedesignCTAVariant.value) return false;
	if (gridMoreInfoVariant.value) return false;
	if (expPreRollActive.value) return false;
	if (appleBingeExpVariant.value) return false;
	return (
		!isProUser.value &&
		!props.inline &&
		!props.isBannerBuyBox &&
		// either placement is overwritten by yo, or the programmatic decision checks
		catalogueItem.value &&
		// show QTV on old buybox when SQTV is active
		catalogueItem.value.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const isSqtvActive = computed(() => {
	if (hasSponsoredRecommendation.value) return false;
	if (expGridSwiperActive.value) return false;
	if (gridRedesignCTAVariant.value) return false;
	if (gridMoreInfoVariant.value) return false;
	if (expPreRollActive.value) return false;
	if (appleBingeExpVariant.value) return false;
	if (isProUser.value || props.inline) return false;

	return !!catalogueItem.value && catalogueItem.value.placement === 'sqtv';
});

const { isSQTVEmpty } = useSingleQTV(
	() => props.title,
	() => promotionProviderShortName.value || null,
	isSqtvActive
);

const showSQTV = computed(() => isSqtvActive.value && !isSQTVEmpty.value);

const freeTrialActive = computed(() => {
	if (expGridSwiperActive.value) return false;
	if (gridRedesignCTAVariant.value) return true;
	if (gridMoreInfoVariant.value) return true;
	if (hasSponsoredRecommendation.value) return false;
	if (expPreRollActive.value) return false;
	if (appleBingeExpVariant.value) return true;
	if (!!catalogueItem.value?.placement) {
		if (isSqtvActive.value) return isSQTVEmpty.value;
		// additional exception: banner, search & guide page buyboxes always render FT
		return !!props.inline || catalogueItem.value?.placement === 'ft';
	}

	return !catalogueItem.value?.placement && !qualityTvActive.value && !showSQTV.value;
});

const promotionShortName = computed(() => {
	if (!!catalogueItem.value?.placement) {
		return catalogueItem.value.shortName;
	}

	return null;
});

// Grid Redesign Exps
const offerRows = computed(() => [
	{
		key: 'stream',
		class: 'stream',
		offers: isFreeBuyBox.value ? filteredOffersFree.value : filteredOffersSorted.value,
		bundles: isFreeBuyBox.value ? [] : filteredOffers.value.bundles,
		label: isFreeBuyBox.value ? 'WEBAPP_FREE' : 'WEBAPP_STREAM',
		show: true,
	},
	{
		key: 'rent',
		class: 'rent',
		offers: filteredOffers.value.rent,
		label: 'WEBAPP_RENT',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'buy',
		class: 'buy',
		offers: filteredOffers.value.buy,
		label: 'WEBAPP_BUY',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'cinema',
		class: 'cinema',
		offers: filteredOffers.value.cinema,
		label: 'WEBAPP_CINEMA',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'fast',
		class: 'fast',
		offers: fastOffers.value,
		label: 'WEBAPP_FREE_TV',
		show: showFastRow.value,
	},
]);
// Grid Redesign Exps

function setEpisodeBuyboxPresentationType(presentationType: BuyboxPresentationType | null) {
	episodeBuyBoxPresentationType.value = presentationType;
}

function clickOut(payload: { buyboxOffer: OfferGraphql; isLeavingIconShown: boolean; offerLocation?: OfferLocation }) {
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer);

	if (
		!!sponsoredRecommendationPackageId.value &&
		payload.buyboxOffer.package.packageId === sponsoredRecommendationPackageId.value
	) {
		let contexts: SnowplowContext[] = [];
		if (payload.buyboxOffer) {
			contexts = trackingContexts.value;
			contexts.push(clickoutContext);
		}

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredRecommendationPackageId.value}`,
				property: 'buybox',
				value: sponsoredRecommendationCohort.value,
			},
			contexts
		);

		trackSraEvent({
			action: SraEventAction.BuyboxClickout,
			sponsoredAd: props.sponsoredAd,
			jwEntityID: props.titleId,
		});
	}
}

function getRentAndBuyOffers(
	titleOffers: AssertByTypename<GetTitleOffersQuery['node'], 'Episode' | 'Movie' | 'Season' | 'Show'>
) {
	const isDigital = (offer: any) =>
		![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(offer.presentationType);

	const dvdRent = titleOffers.dvdBluRay
		.filter(o => o.monetizationType === MonetizationType.Rent && !isDigital(o))
		.sort((a, b) => (a.retailPrice || '').localeCompare(b.retailPrice || '', undefined, { numeric: true }));

	const dvdBuy = titleOffers.dvdBluRay.filter(o => o.monetizationType === MonetizationType.Buy && !isDigital(o));

	return {
		rent: [...titleOffers.rent.filter(isDigital), ...dvdRent],
		buy: [...titleOffers.buy.filter(isDigital), ...dvdBuy],
	};
}

watch(loading, () => {
	if (loading.value) {
		emits('buyboxLoading', true);
	}
});

onMounted(() => {
	if (activeRoute.value?.query.monetization_types === 'free') {
		setBuyboxPresentationType({ presentationType: MonetizationType.Free });
	}
});

const { isGridCategorySplitActive } = useGridCategorySplit({
	objectType: () => props.titleObjectType,
	buyBoxActiveType: buyBoxActiveType,
});

const subscriptionLabels: Record<string, string> = {
	de: 'Abo',
	fr: 'Abo',
	it: 'Abbo',
	es: 'Susc.',
	en: 'Subscription',
};
const subscriptionLabel = computed(() => {
	return subscriptionLabels[language.value] || subscriptionLabels.en;
});

const subscriptionOffers = computed(() => {
	return filteredOffers.value.stream.filter(
		offer =>
			offer.monetizationType === MonetizationType.Flatrate ||
			offer.monetizationType === MonetizationType.FlatrateAndBuy
	);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';
@import '~@/styles/variables.scss';

.buybox {
	&__heading {
		display: flex;
		align-items: center;
		justify-content: space-between;

		.buybox__heading__country-switcher {
			display: block;
			@media (min-width: 500px) {
				display: none;
			}
		}

		&__text {
			font-weight: 700;
			text-transform: uppercase;
			font-size: 16px;
			color: $color-text-dark;
			margin: 0;
			margin-block: 16px 8px;
		}
	}

	&__filter {
		order: -10; // Ensure filter is always first, even with variant reordering
	}

	&__content > * + * {
		margin-top: 0;
		border-bottom: 1px solid rgba(255, 255, 255, 0.08);
	}

	&__content > :last-child {
		margin-top: 8px;
		border: none;
	}
	&__content.exclude-last-line :nth-last-child(3) {
		border: none;
	}

	&__content {
		display: flex;
		flex-direction: column;
		// here we change the order of rent offers to be first in the list when JWTV campaign is active
		&.rent-offers-first {
			> .rent {
				order: -1;
			}
		}

		// Subs > Free > Rent > Buy
		&.grid-category-split {
			> .subscription {
				order: -4;
				margin-bottom: 0;
			}
			> .free {
				order: -3;
				margin-bottom: 0;
			}
			> .rent {
				order: -2;
				margin-bottom: 0;
			}
			> .buy {
				order: -1;
				margin-bottom: 0;
			}
		}

		.no-border {
			border: none;
		}
	}

	&__content.inline {
		display: flex;
		flex-direction: row;
		gap: 16px;
		overflow-x: auto;
		> * + * {
			margin-top: 0;
			border: none;
		}
	}

	.buybox-text {
		word-wrap: break-word;
		line-height: initial;
	}

	&__content__timestamp {
		font-size: 13.5px;
		margin: 0;
		margin-inline-end: 3px;
		border: none;

		@media (max-width: 550px) {
			font-size: 11px;
		}
	}
}

.switchers-container {
	display: flex;
	align-items: center;
	gap: 8px;
}
</style>
